<template>
  <component
    :is="linkTag"
    v-bind="linkAttrs"
    class="brand-logo d-inline-flex align-center"
  >
    <v-img
      v-if="src"
      :src="src"
      :width="computedWidth"
      :alt="alt"
      :position="position"
      class="brand-logo__image"
      contain
    >
      <template #placeholder>
        <div :style="placeholderStyle" />
      </template>
    </v-img>
  </component>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  src: { type: String, default: '' },
  height: { type: [Number, String], required: true },
  maxWidth: { type: [Number, String], default: null },
  alt: { type: String, default: '' },
  link: { type: String, default: '' },
  position: { type: String, default: 'center center' },
})

const naturalRatio = ref(null)
let activeLoadToken = 0

const resolvedHeight = computed(() => {
  const parsedHeight = Number(props.height)
  return Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : 0
})

const resolvedMaxWidth = computed(() => {
  if (props.maxWidth === null || props.maxWidth === undefined || props.maxWidth === '') return null

  const parsedWidth = Number(props.maxWidth)
  return Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : null
})

const fallbackWidth = computed(() => resolvedMaxWidth.value || resolvedHeight.value)

const computedWidth = computed(() => {
  if (!resolvedHeight.value) return 0

  if (!naturalRatio.value) return fallbackWidth.value

  const calculatedWidth = Math.round(resolvedHeight.value * naturalRatio.value)
  return resolvedMaxWidth.value
    ? Math.min(calculatedWidth, resolvedMaxWidth.value)
    : calculatedWidth
})

const normalizedLink = computed(() => String(props.link || '').trim())
const linkTag = computed(() => normalizedLink.value ? 'a' : 'div')
const linkAttrs = computed(() => (
  normalizedLink.value
    ? {
        href: normalizedLink.value,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {}
))

const placeholderStyle = computed(() => ({
  width: `${computedWidth.value || fallbackWidth.value || resolvedHeight.value}px`,
  height: `${resolvedHeight.value}px`,
}))

watch(
  () => props.src,
  (src) => {
    activeLoadToken += 1
    const loadToken = activeLoadToken
    naturalRatio.value = null

    if (!src || typeof Image === 'undefined') return

    const image = new Image()
    image.onload = () => {
      if (loadToken !== activeLoadToken || !image.naturalHeight) return
      naturalRatio.value = image.naturalWidth / image.naturalHeight
    }
    image.onerror = () => {
      if (loadToken !== activeLoadToken) return
      naturalRatio.value = null
    }
    image.src = src
  },
  { immediate: true },
)
</script>

<style scoped>
.brand-logo {
  max-width: 100%;
  min-width: 0;
}

.brand-logo__image {
  max-width: 100%;
}
</style>
